/* eslint-disable @typescript-eslint/naming-convention */

import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Image,
  Stack,
  Text
} from "@chakra-ui/react";

import { QuestionOutlineIcon } from "@chakra-ui/icons";
import { format } from "date-fns";
import { MdInfoOutline } from "react-icons/md";

import {
  IAccountingDataStatus,
  IIntegrationsStatus
} from "@/jenesys-types/jenesys-entities/config";

import { NamedLink } from "@/components/common/navigation/NamedLink";
import Barrier from "@/components/common/permission/Barrier";
import { MoreInfo } from "@/components/routes/company/MoreInfo";
import { isProductionOrStaging } from "@/util/environments";
import {
  accountingDataStatusStyles,
  accountingPlatforms,
  addTimeZoneToTimestamp,
  getAccountingDataStatusMessage
} from "@/util/helpers";

interface IntegrationDetailsProps {
  company: any;
  integration: IIntegrationsStatus & {
    platform: (typeof accountingPlatforms)[0];
  };
  handleAuthorize: (name: string) => any;
  isFetchingAuthURL?: boolean;
  accountingDataStatus?: IAccountingDataStatus | null;
  selectedClient: any;
}

const integrationsToHideInProd = ["sage"];
const productionOrStaging = isProductionOrStaging();

export const IntegrationDetails: React.FC<IntegrationDetailsProps> = ({
  company,
  integration,
  handleAuthorize,
  isFetchingAuthURL,
  accountingDataStatus,
  selectedClient
}) => {
  const {
    last_refreshed,
    tenant_name,
    subscription_plan,
    initially_connected_user,
    platform,
    company_status,
    is_vat_registered
  } = integration;

  const vatConflict =
    company &&
    company_status === "valid" &&
    is_vat_registered !== company?.is_vat_registered;

  const isIntegrationHiddenInProd = integrationsToHideInProd.includes(
    platform.name
  );

  if (isIntegrationHiddenInProd) {
    return null;
  }

  return (
    <Stack
      px={6}
      opacity={platform.isDisabled ? 0.4 : 1}
      py={6}
      spacing={6}
      border="1px solid"
      rounded={6}
      borderColor="blackAlpha.200"
    >
      {vatConflict && (
        <HStack
          rounded={6}
          border="1px solid"
          borderColor="primary.500"
          py={3}
          px={6}
        >
          <Icon fontSize={22} as={MdInfoOutline} />
          <Text fontSize={12} fontWeight={500}>
            A discrepancy exists between the VAT registered status in&nbsp;
            {platform.displayName} and Jack. Please reconnect to&nbsp;
            {platform.displayName}
            {!!subscription_plan && ` (${subscription_plan})`}
          </Text>
        </HStack>
      )}

      <Stack
        w="full"
        justify="space-between"
        direction={["column", "column", "column", "row"]}
      >
        <HStack spacing={31} justify="flex-start">
          <Image
            alt={platform.name}
            src={platform.imageLink}
            width={12}
            height={12}
          />
          <Box>
            <Text fontSize={20} fontWeight={600}>
              {platform.displayName}
              {!!subscription_plan && ` (${subscription_plan})`}
            </Text>

            {tenant_name && (
              <Text fontSize={16} fontWeight={500}>
                {`Connected to ${platform.displayName}`}
                {tenant_name && `: ${tenant_name}`}
                {initially_connected_user?.first_name &&
                  initially_connected_user?.last_name &&
                  ` by ${initially_connected_user.first_name} ${initially_connected_user.last_name}`}
              </Text>
            )}

            <Text maxW={480} fontSize={12} fontWeight={400}>
              {platform.description}
            </Text>
          </Box>
        </HStack>

        {!platform.isDisabled && (
          <Box>
            {company_status !== "valid" ? (
              <Barrier show when="company.connections.accounting">
                <HStack
                  pointerEvents={isFetchingAuthURL ? "none" : "initial"}
                  opacity={isFetchingAuthURL ? 0.4 : 1}
                  justify="space-between"
                >
                  {platform.connectImage ? (
                    <Image
                      cursor="pointer"
                      alt="Connect"
                      src={platform.connectImage}
                      width={222}
                      height={43}
                      onClick={() => handleAuthorize(platform.name)}
                    />
                  ) : (
                    <Button
                      h={43}
                      variant="default"
                      color="white"
                      bg={platform.primaryButtonColor}
                      minW={222}
                      pl={3}
                      justifyContent="flex-start"
                      rounded={4}
                      iconSpacing={4}
                      leftIcon={
                        <Center p={1} rounded="full" bg="white">
                          <Image
                            alt={platform.name}
                            src={platform.imageLink}
                            width={6}
                            height={6}
                          />
                        </Center>
                      }
                      size="sm"
                      onClick={() => handleAuthorize(platform.name)}
                    >
                      Connect to {platform.displayName}
                    </Button>
                  )}
                </HStack>
              </Barrier>
            ) : (
              <Stack w={222} align="flex-end" justify="space-between">
                <Barrier show when="company.connections.accounting">
                  <Button
                    as={NamedLink}
                    name="settings.client.connections.detail"
                    params={{
                      category: platform.category,
                      platform: platform.name
                    }}
                    h={7}
                    colorScheme="primary"
                    w={95}
                    size="sm"
                    variant="outline"
                  >
                    Manage
                  </Button>
                </Barrier>
                {!!last_refreshed && (
                  <Text
                    textAlign="right"
                    fontStyle="italic"
                    fontSize={12}
                    fontWeight={400}
                  >
                    Last refreshed:&nbsp;
                    {format(
                      new Date(addTimeZoneToTimestamp(last_refreshed)),
                      "MMMM do, yyyy 'at' hh:mm:ss a"
                    )}
                  </Text>
                )}
                {!platform.isDisabled &&
                  company_status === "valid" &&
                  !!accountingDataStatus?.status && (
                    <HStack>
                      <MoreInfo
                        title="Accounting Data Status"
                        text={getAccountingDataStatusMessage(
                          accountingDataStatus,
                          selectedClient?.name
                        )}
                        icon={QuestionOutlineIcon}
                      />

                      <Center
                        rounded="full"
                        px={3}
                        w="fit-content"
                        fontWeight={500}
                        py={2}
                        h={25}
                        fontSize={12}
                        mt={0}
                        bg={
                          accountingDataStatusStyles[
                            accountingDataStatus?.status
                          ]?.bg
                        }
                        color={
                          accountingDataStatusStyles[
                            accountingDataStatus?.status
                          ]?.color
                        }
                      >
                        <Icon
                          cursor="pointer"
                          color={
                            accountingDataStatusStyles[
                              accountingDataStatus?.status
                            ]?.text
                          }
                          as={
                            accountingDataStatusStyles[
                              accountingDataStatus?.status
                            ]?.icon
                          }
                        />
                        <Text ml={1}>
                          {
                            accountingDataStatusStyles[
                              accountingDataStatus?.status
                            ]?.text
                          }
                        </Text>
                      </Center>
                    </HStack>
                  )}
              </Stack>
            )}
          </Box>
        )}

        {platform.isDisabled && (
          <Center>
            <Text
              fontStyle="italic"
              fontSize={12}
              color="primary.300"
              maxW={["full", "full", "full", 250]}
              fontWeight={400}
            >
              Coming soon...
            </Text>
          </Center>
        )}
      </Stack>
    </Stack>
  );
};
